/**
 * Hook per sincronizzare lo stato di autenticazione con OneSignal
 * 
 * Gestisce l'aggiornamento dell'external_user_id quando l'utente
 * fa login/logout, garantendo il corretto targeting delle notifiche.
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { oneSignalRegistrationService } from '@/services/notifications/oneSignalRegistrationService';
import { debugLog, debugError } from '@/utils/debugLogger';

export const useAuthSync = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const hasLoggedInOnce = useRef(false);
  const lastUserId = useRef<string | null>(null);

  useEffect(() => {
    // Non fare nulla mentre lo stato di auth è in caricamento
    if (isLoading) {
      debugLog('auth', 'Auth still loading, skipping sync', undefined, 'useAuthSync');
      return;
    }

    const handleAuthChange = async () => {
      try {
        if (isAuthenticated && user?.id) {
          // Evita sincronizzazioni duplicate per lo stesso utente
          if (lastUserId.current === user.id) {
            debugLog('auth', 'Same user, skipping sync', undefined, 'useAuthSync');
            return;
          }
          
          lastUserId.current = user.id;
          hasLoggedInOnce.current = true;
          
          debugLog('auth', 'User logged in, syncing with OneSignal SDK', undefined, 'useAuthSync');
          
          try {
            // Dynamic import — notificationService fuori dal bundle iniziale
            const { notificationService } = await import('@/services/notifications/notificationService');

            // 🛡️ FIX: Attendere l'inizializzazione COMPLETA di OneSignal
            const isOneSignalReady = await notificationService.init();
            
            if (!isOneSignalReady) {
              debugLog('auth', 'OneSignal not ready, skipping sync', undefined, 'useAuthSync');
              return;
            }
            
            // Ora è sicuro chiamare setExternalUserId
            await notificationService.setExternalUserId(user.id);
                  
            // Salva il Player ID corrente nel database
            const playerId = await notificationService.getUserId();
            if (playerId) {
              await oneSignalRegistrationService.savePlayerId(playerId);
              debugLog('auth', `Player ID saved to database: ${playerId.substring(0, 8)}...`, undefined, 'useAuthSync');
              
              // Aggiorna tag user_type a 'authenticated'
              await notificationService.sendTag('user_type', 'authenticated');
            }
            
            debugLog('auth', 'User login sync completed successfully', undefined, 'useAuthSync');
          } catch (error) {
            debugError('auth', 'Failed to sync user login:', error, 'useAuthSync');
          }
          
        } else if (hasLoggedInOnce.current && lastUserId.current !== null) {
          debugLog('auth', 'User logged out, clearing OneSignal external_user_id', undefined, 'useAuthSync');
          lastUserId.current = null;
          
          try {
            // Dynamic import — notificationService fuori dal bundle iniziale
            const { notificationService } = await import('@/services/notifications/notificationService');
            await notificationService.removeExternalUserId();
            
            // Imposta tag user_type a 'anonymous' dopo logout
            await notificationService.sendTag('user_type', 'anonymous');
            
            debugLog('auth', 'User logout sync completed successfully', undefined, 'useAuthSync');
          } catch (error) {
            debugError('auth', 'Failed to sync user logout:', error, 'useAuthSync');
          }
        } else {
          debugLog('auth', 'Initial load with no auth or no previous login, skipping logout sync', undefined, 'useAuthSync');
        }
      } catch (error) {
        debugError('auth', 'Error syncing auth state with OneSignal:', error, 'useAuthSync');
      }
    };

    // 🛡️ FIX: Defer to next tick to avoid setState during Suspense hydration
    const timeoutId = setTimeout(() => {
      handleAuthChange();
    }, 0);
    
    return () => clearTimeout(timeoutId);
  }, [isAuthenticated, user?.id, isLoading]);

  return {
    isAuthenticated,
    userId: user?.id || null
  };
};
